import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import GenericCasinoTimer from "../../components/GenericCasinoTimer";
import { getUniversalCasinoPlExposureByRunner } from "./genericFunctions";
import {
  getCasinoExposureAPI,
  getUniversalCasinoLastResultAPI,
  getUniversalCasinoOddsAPI,
  uniCasinoBetAPI,
} from "../../service/casino";
import "./LuckyZeroToNine.css";
import {
  luckyResultWinner,
  normalizeLuckyResults,
} from "./luckyZeroToNineResults.js";

const GAME_ID = "UNI_LUCKY_0_TO_9";
const STAKES = [
  { img: "chips1", amount: 100, label: "100" },
  { img: "chips1", amount: 500, label: "500" },
  { img: "chips1k", amount: 1000, label: "1k" },
  { img: "chips5", amount: 5000, label: "5k" },
  { img: "chips10", amount: 10000, label: "10k" },
  { img: "chips20", amount: 25000, label: "25k" },
  { img: "chips1k", amount: 50000, label: "50k" },
  { img: "chips1k", amount: 100000, label: "1L" },
];

const normalizeStatus = (value) => String(value || "").trim().toUpperCase();
const isTableOnline = (table) => ["OPEN", "ONLINE"].includes(normalizeStatus(table?.status));
const isRunnerActive = (runner) =>
  ["ACTIVE", "OPEN", "ONLINE"].includes(normalizeStatus(runner?.status)) &&
  Number.isFinite(Number(runner?.back1)) &&
  Number(runner.back1) > 1;

const normalizeMarkets = (markets) =>
  (Array.isArray(markets) ? markets : [])
    .filter((market) => market && typeof market === "object")
    .map((market) => ({
      ...market,
      oddDetailsDTO: (Array.isArray(market.oddDetailsDTO) ? market.oddDetailsDTO : [])
        .filter((runner) => runner && typeof runner === "object"),
    }));

const runnerColor = (runnerName) => {
  const name = String(runnerName).toUpperCase();
  if (name === "0") return "l09-green";
  if (name === "RED" || ["1", "3", "5", "7", "9"].includes(name)) return "l09-red";
  return "l09-black";
};

export default function LuckyZeroToNine() {
  const [odds, setOdds] = useState(null);
  const [results, setResults] = useState([]);
  const [exposure, setExposure] = useState([]);
  const [stake, setStake] = useState(0);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedError, setFeedError] = useState(false);
  const [resultsError, setResultsError] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const latestOdds = useRef(null);
  const busy = useRef(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    let active = true;
    const timers = new Set();
    const poll = async (request, delay) => {
      await request();
      if (!active) return;
      const timer = setTimeout(() => {
        timers.delete(timer);
        poll(request, delay);
      }, delay);
      timers.add(timer);
    };

    poll(async () => {
      try {
        const data = await getUniversalCasinoOddsAPI(GAME_ID);
        if (!active) return;
        if (data?.code !== 200 || !data?.response?.roundId) throw new Error();
        const next = {
          ...data.response,
          markets: normalizeMarkets(data.response.markets),
        };
        latestOdds.current = next;
        setOdds(next);
        setFeedError(false);
        setSelected((previous) =>
          previous && String(previous.marketId) === String(next.roundId) ? previous : null
        );
      } catch {
        if (active) setFeedError(true);
      }
    }, 800);

    poll(async () => {
      try {
        const data = await getUniversalCasinoLastResultAPI(GAME_ID);
        if (!active) return;
        if (data?.code !== 200) throw new Error();
        setResults(normalizeLuckyResults(data.response));
        setResultsError(false);
      } catch {
        if (active) setResultsError(true);
      }
    }, 5000);

    poll(async () => {
      const roundId = latestOdds.current?.roundId;
      if (!roundId) return;
      try {
        const data = await getCasinoExposureAPI(roundId);
        if (active && String(latestOdds.current?.roundId) === String(roundId)) {
          setExposure(Array.isArray(data?.response) ? data.response : []);
        }
      } catch {
        // Keep the previous exposure while the next poll retries.
      }
    }, 2000);

    return () => {
      active = false;
      mounted.current = false;
      timers.forEach(clearTimeout);
    };
  }, []);

  useEffect(() => setExposure([]), [odds?.roundId]);

  const tableOnline = !feedError && isTableOnline(odds);
  const markets = Array.isArray(odds?.markets) ? odds.markets : [];
  const latestResult = results[0];
  const latestWinner = luckyResultWinner(latestResult);
  const winningNumber = /^[0-9]$/.test(latestWinner)
    ? Number(latestWinner)
    : null;
  const wheelResultKey = `${latestResult?.roundId || "waiting"}-${latestWinner || "none"}`;
  const wheelEndRotation = winningNumber === null
    ? "0deg"
    : `${1800 + ((360 - winningNumber * 36) % 360)}deg`;

  const placeBet = async (selection) => {
    if (busy.current || !selection || !stake) return;
    const current = latestOdds.current;
    if (!isTableOnline(current) || String(current?.roundId) !== String(selection.marketId)) return;

    const market = current.markets.find((item) =>
      item.oddDetailsDTO.some((runner) =>
        `${item.marketId}_${runner.selectionId}` === selection.marketIdWithSelectionId
      )
    );
    const runner = market?.oddDetailsDTO.find((item) =>
      `${market.marketId}_${item.selectionId}` === selection.marketIdWithSelectionId
    );
    if (!market || !isRunnerActive(runner)) return;

    const amount = Number(stake);
    const min = Number(market.min);
    const max = Number(market.max);
    if ((Number.isFinite(min) && amount < min) || (Number.isFinite(max) && amount > max)) {
      toast.error(`Enter a stake between ${market.min} and ${market.max}.`);
      return;
    }

    const payload = { ...selection, amount, back: runner.back1 };
    setSelected(payload);
    busy.current = true;
    setSubmitting(true);
    try {
      const data = await uniCasinoBetAPI(payload);
      if (!mounted.current) return;
      if (data?.code === 200) toast.success("Bet placed successfully");
      else toast.error(typeof data?.response === "string" ? data.response : "Bet could not be placed.");
    } catch {
      if (mounted.current) toast.error("Unable to confirm the bet. Check your bets before trying again.");
    } finally {
      busy.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };

  const selectRunner = (market, runner) => {
    if (busy.current || !tableOnline || !isRunnerActive(runner)) return;
    const selection = {
      amount: stake,
      back: runner.back1,
      casinoGames: GAME_ID,
      marketId: odds.roundId,
      marketIdWithSelectionId: `${market.marketId}_${runner.selectionId}`,
      runnerName: runner.runnerName,
      type: "back",
    };
    setSelected(selection);
    if (stake) placeBet(selection);
  };

  return (
    <div className="l09-page">
      <div
        className="l09-wheel-stage"
        style={{
          backgroundImage: "url(https://casino.royall444.com/assets/img/wheelmainbg.gif)",
        }}
      >
        <span className="l09-round">RID: {odds?.roundId || "—"}</span>
        <div className="l09-timer">
          <GenericCasinoTimer
            time={odds?.seconds}
            status={tableOnline ? "ONLINE" : "SUSPEND"}
            black
          />
        </div>
        <div className="l09-wheel-wrap">
          <div className="l09-wheel-pointer" aria-hidden="true" />
          <div className="l09-wheel-frame">
            <div
              key={wheelResultKey}
              className={`l09-wheel ${winningNumber !== null ? "l09-wheel-spinning" : ""}`}
              style={{ "--l09-wheel-end": wheelEndRotation }}
              aria-label={winningNumber === null ? "Waiting for latest result" : `Winning number ${winningNumber}`}
            >
              {Array.from({ length: 10 }, (_, number) => {
                const angle = -90 + number * 36;
                const radians = angle * Math.PI / 180;
                return (
                  <span
                    className="l09-wheel-number"
                    key={number}
                  style={{
                    left: `${50 + Math.cos(radians) * 38}%`,
                    top: `${50 + Math.sin(radians) * 38}%`,
                    transform: `translate(-50%, -50%) rotate(${number * 36}deg)`,
                  }}
                  >
                    {number}
                  </span>
                );
              })}
              <span className="l09-wheel-hub" aria-hidden="true">U</span>
            </div>
          </div>
        </div>
        {winningNumber !== null && (
          <div key={`winner-${wheelResultKey}`} className="l09-wheel-winner">
            Winner: {winningNumber}
          </div>
        )}
      </div>

      {feedError && <p className="l09-feed-error">Live odds unavailable. Reconnecting…</p>}

      <div className="l09-markets">
        {markets.map((market) => (
          <section className={`l09-market l09-${market.marketName?.toLowerCase().replace(/[^a-z]+/g, "-")}`} key={market.marketId}>
            <h2>{market.marketName}</h2>
            <div className="l09-runners">
              {market.oddDetailsDTO.map((runner) => {
                const selectionKey = `${market.marketId}_${runner.selectionId}`;
                const locked = !tableOnline || !isRunnerActive(runner);
                return (
                  <div className="l09-runner-wrap" key={selectionKey}>
                    <span className="l09-exposure">
                      {getUniversalCasinoPlExposureByRunner(
                        exposure,
                        runner.runnerName,
                        runner.selectionId
                      )}
                    </span>
                    <button
                      type="button"
                      className={`l09-runner ${runnerColor(runner.runnerName)} ${selected?.marketIdWithSelectionId === selectionKey ? "l09-selected" : ""}`}
                      disabled={locked}
                      onClick={() => selectRunner(market, runner)}
                    >
                      <strong>{runner.runnerName}</strong>
                      <span>{Number.isFinite(Number(runner.back1)) ? Number(runner.back1).toFixed(2) : "—"}</span>
                      {locked && <span className="l09-lock" aria-label="Betting locked"><i className="fa fa-lock" aria-hidden="true" /></span>}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <section className="l09-chip-zone" aria-label="Select stake">
        <ul className="chips">
          {STAKES.map((item) => (
            <li
              key={item.amount}
              className={`chips_1 ${stake === item.amount ? "now" : ""}`}
              role="button"
              tabIndex={submitting ? -1 : 0}
              aria-pressed={stake === item.amount}
              aria-disabled={submitting}
              onClick={() => { if (!submitting) setStake(item.amount); }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  if (!submitting) setStake(item.amount);
                }
              }}
            >
              <img src={`https://casino.queenexch.com/assets/chips/${item.img}.svg`} alt="" />
              <p>{item.label}</p>
              <span className="ring">ring</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="l09-results">
        <h2>Recent Result</h2>
        <ul>
          {results.map((result, index) => {
            const winner = luckyResultWinner(result);
            return (
              <li
                key={`${result?.roundId || "result"}-${index}`}
                className={runnerColor(winner)}
                title={`Round ${result?.roundId || "—"}`}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedResult(result)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedResult(result);
                  }
                }}
              >
                {winner || "—"}
              </li>
            );
          })}
        </ul>
        {!results.length && !resultsError && <span>No recent results</span>}
        {resultsError && <span>Results unavailable</span>}
      </section>

      {selectedResult && (
        <div
          className="l09-result-backdrop"
          role="presentation"
          onClick={() => setSelectedResult(null)}
        >
          <section
            className="l09-result-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="l09-result-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="l09-result-close"
              aria-label="Close result"
              onClick={() => setSelectedResult(null)}
            >
              ×
            </button>
            <h2 id="l09-result-title">Lucky 0 To 9 Result</h2>
            <p>Round ID: <strong>{selectedResult.roundId || "—"}</strong></p>
            <div className={`l09-result-winner ${runnerColor(luckyResultWinner(selectedResult))}`}>
              {luckyResultWinner(selectedResult) || "—"}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
